import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import CheckoutModal from '@/components/ds-checkout-modal'
import { usePaymentContext } from '@/components/ds-checkout-modal/payment-context'
import { purchase } from '@/models/plans/apis'
import { DialogEventHandler } from '@/types'
import queryClient from '@/components/query-client'
import TrustScoreAgreementModal from './trust-score-agreement-modal'
import TrustScoreAgreementNoDataModal from './trust-score-agreement-modal-no-data'

type DialogKeys =
  | 'trustScore'
  | 'editPaymentDialog'
  | 'trustScoreAgreementDialog'
  | 'trustScoreAgreementNoDataDialog'
type Props = {
  applicationId: string
  getNewTrustScore: () => void
  products: {
    products: { price_id?: string; plan_name?: string }[]
  }
  visible: {
    trustScore: boolean
    editPaymentDialog: boolean
    trustScoreAgreementDialog: boolean
    trustScoreAgreementNoDataDialog: boolean
  }
  showModal: (id: DialogKeys) => DialogEventHandler
  closeModal: (id: DialogKeys) => DialogEventHandler
  hasUnlockCredits: boolean
  unlockTrustScore: () => void
}
export const TrustScorePaymentModal = ({
  applicationId,
  getNewTrustScore,
  products,
  visible,
  showModal,
  closeModal,
  hasUnlockCredits,
  unlockTrustScore
}: Props) => {
  const { t } = useTranslation()
  const { setProduct, setPurchase } = usePaymentContext()

  const formatProduct = (product: { price_id?: string; plan_name?: string }) => {
    return {
      items: [
        {
          priceId: product?.price_id,
          quantity: 1,
          useCredits: false
        }
      ],
      metadata: {
        plan_name: product?.plan_name
      }
    }
  }

  useEffect(() => {
    if (products) {
      setProduct(formatProduct(products?.products[0]))
      const processPurchase = async (paymentMethodId: string) => {
        const result = await purchase(paymentMethodId, applicationId, {
          unlocked: 1,
          viewed: 1,
          source: 'credit_checks_additional_packages'
        })

        if (result.response?.response.ok && result.update?.response.ok) {
          // Get trust Score
          getNewTrustScore()
        } else {
          throw Error(result?.response?.body.message || result?.update?.body.message)
        }
      }
      setPurchase(() => processPurchase)
    }
  }, [applicationId, closeModal, getNewTrustScore, products, setProduct, setPurchase])

  const closeTrustScoreAgreementDialog = () => {
    queryClient.invalidateQueries({
      queryKey: ['credit-report', applicationId]
    })
    closeModal('trustScoreAgreementDialog')()
  }

  return (
    <>
      <TrustScoreAgreementModal
        visible={visible.trustScoreAgreementDialog}
        onClose={closeTrustScoreAgreementDialog}
        submitConfirmButton={hasUnlockCredits ? unlockTrustScore : showModal('trustScore')}
        applicationId={applicationId}
      />
      <TrustScoreAgreementNoDataModal
        visible={visible.trustScoreAgreementNoDataDialog}
        onClose={closeModal('trustScoreAgreementNoDataDialog')}
        submitConfirmButton={hasUnlockCredits ? unlockTrustScore : showModal('trustScore')}
      />
      <CheckoutModal
        visible={visible.trustScore}
        title={t('l.credit_usage.product.tenant_screening.title', 'Tenant Screening Report')}
        closeModal={closeModal('trustScore')}
      />
    </>
  )
}
